import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, Plus, Trash2, Pencil } from "lucide-react";
import {
  adminListPosts,
  adminUpsertPost,
  adminDeletePost,
  adminUpdateBlogContent,
  adminRestoreWixLists,
  getBlogPageContent,
  type AdminBlogPost,
} from "@/lib/blog.functions";

export const Route = createFileRoute("/_authenticated/admin/blog")({
  head: () => ({ meta: [{ title: "Admin · Blog" }, { name: "robots", content: "noindex" }] }),
  component: AdminBlogPage,
});

function AdminBlogPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const listFn = useServerFn(adminListPosts);
  const upsertFn = useServerFn(adminUpsertPost);
  const deleteFn = useServerFn(adminDeletePost);
  const contentFn = useServerFn(getBlogPageContent);
  const updateContentFn = useServerFn(adminUpdateBlogContent);

  const { data: posts, isLoading } = useQuery({
    queryKey: ["admin", "blog-posts"],
    queryFn: () => listFn(),
  });
  const { data: pageContent } = useQuery({
    queryKey: ["blog", "content"],
    queryFn: () => contentFn(),
  });

  const [editing, setEditing] = useState<AdminBlogPost | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin", "blog-posts"] });
    qc.invalidateQueries({ queryKey: ["blog"] });
  };

  const del = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); refresh(); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  if (editing || creating) {
    return (
      <PostEditor
        post={editing}
        onCancel={() => { setEditing(null); setCreating(false); }}
        onSave={async (payload) => {
          try {
            await upsertFn({ data: payload });
            toast.success("Saved");
            setEditing(null); setCreating(false);
            refresh();
          } catch (e: any) { toast.error(e.message ?? "Failed"); }
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 overflow-y-auto bg-background text-foreground" style={{ touchAction: "auto" }}>
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/admin" })}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-2xl font-serif">Blog</h1>
          </div>
          <div className="flex items-center gap-2">
            <RestoreWixListsButton onDone={refresh} />
            <Button onClick={() => setCreating(true)} size="sm">
              <Plus className="w-4 h-4 mr-1" /> New post
            </Button>
          </div>
        </div>

        <BlogContentSection
          initial={pageContent}
          onSave={async (v) => {
            try {
              await updateContentFn({ data: v });
              toast.success("Page settings saved");
              qc.invalidateQueries({ queryKey: ["blog"] });
            } catch (e: any) { toast.error(e.message ?? "Failed"); }
          }}
        />

        <section className="space-y-3">
          <h2 className="text-lg font-serif">Posts</h2>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !posts || posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No posts yet.</p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden">
              {posts.map((p) => (
                <li key={p.id} className="flex items-center gap-3 p-3 bg-card">
                  {p.cover_image_url ? (
                    <img src={p.cover_image_url} alt="" className="w-14 h-14 rounded object-cover bg-muted" />
                  ) : (
                    <div className="w-14 h-14 rounded bg-muted" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-widest ${p.status === "published" ? "text-green-600" : "text-muted-foreground"}`}>{p.status}</span>
                      <span className="text-xs text-muted-foreground truncate">/{p.slug}</span>
                    </div>
                    <div className="font-serif truncate">{p.title}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Delete "${p.title}"? This cannot be undone.`)) del.mutate(p.id);
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function BlogContentSection({
  initial,
  onSave,
}: {
  initial: { hero_title: string; hero_subtitle: string | null; seo_title: string | null; seo_description: string | null } | undefined;
  onSave: (v: { hero_title: string; hero_subtitle: string | null; seo_title: string | null; seo_description: string | null }) => Promise<void>;
}) {
  const [v, setV] = useState({
    hero_title: "",
    hero_subtitle: "",
    seo_title: "",
    seo_description: "",
  });
  useEffect(() => {
    if (initial) {
      setV({
        hero_title: initial.hero_title ?? "",
        hero_subtitle: initial.hero_subtitle ?? "",
        seo_title: initial.seo_title ?? "",
        seo_description: initial.seo_description ?? "",
      });
    }
  }, [initial]);
  return (
    <section className="space-y-3 border border-border rounded-lg p-4 bg-card">
      <h2 className="text-lg font-serif">Blog page settings</h2>
      <div className="grid gap-3">
        <div>
          <Label>Hero title</Label>
          <Input value={v.hero_title} onChange={(e) => setV({ ...v, hero_title: e.target.value })} />
        </div>
        <div>
          <Label>Hero subtitle</Label>
          <Input value={v.hero_subtitle} onChange={(e) => setV({ ...v, hero_subtitle: e.target.value })} />
        </div>
        <div>
          <Label>SEO title</Label>
          <Input value={v.seo_title} onChange={(e) => setV({ ...v, seo_title: e.target.value })} />
        </div>
        <div>
          <Label>SEO description</Label>
          <Textarea rows={2} value={v.seo_description} onChange={(e) => setV({ ...v, seo_description: e.target.value })} />
        </div>
        <Button
          size="sm"
          className="w-fit"
          onClick={() => onSave({
            hero_title: v.hero_title.trim() || "Stories from Backland",
            hero_subtitle: v.hero_subtitle.trim() || null,
            seo_title: v.seo_title.trim() || null,
            seo_description: v.seo_description.trim() || null,
          })}
        >
          Save page settings
        </Button>
      </div>
    </section>
  );
}

function PostEditor({
  post,
  onCancel,
  onSave,
}: {
  post: AdminBlogPost | null;
  onCancel: () => void;
  onSave: (p: any) => Promise<void>;
}) {
  const [form, setForm] = useState({
    id: post?.id,
    slug: post?.slug ?? "",
    title: post?.title ?? "",
    excerpt: post?.excerpt ?? "",
    content_html: post?.content_html ?? "",
    cover_image_url: post?.cover_image_url ?? "",
    author: post?.author ?? "",
    tags: (post?.tags ?? []).join(", "),
    status: (post?.status as "draft" | "published") ?? "draft",
    published_at: post?.published_at ? post.published_at.slice(0, 10) : "",
    minutes_to_read: post?.minutes_to_read ?? null,
  });

  return (
    <div className="fixed inset-0 overflow-y-auto bg-background text-foreground" style={{ touchAction: "auto" }}>
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <h1 className="text-xl font-serif">{post ? "Edit post" : "New post"}</h1>
          </div>
          <Button
            size="sm"
            onClick={() =>
              onSave({
                ...(form.id ? { id: form.id } : {}),
                slug: form.slug.trim(),
                title: form.title.trim(),
                excerpt: form.excerpt.trim() || null,
                content_html: form.content_html,
                cover_image_url: form.cover_image_url.trim() || null,
                author: form.author.trim() || null,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
                status: form.status,
                published_at:
                  form.status === "published"
                    ? (form.published_at ? new Date(form.published_at).toISOString() : new Date().toISOString())
                    : (form.published_at ? new Date(form.published_at).toISOString() : null),
                minutes_to_read: form.minutes_to_read ?? null,
              })
            }
          >
            Save
          </Button>
        </div>

        <div className="grid gap-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <Label>Slug (URL)</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
              placeholder="my-post-slug"
            />
            <p className="text-xs text-muted-foreground mt-1">/blog/{form.slug || "your-slug"}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as any })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Publish date</Label>
              <Input type="date" value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Author</Label>
            <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
          </div>
          <div>
            <Label>Cover image URL</Label>
            <Input value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} placeholder="https://…" />
            {form.cover_image_url && (
              <img src={form.cover_image_url} alt="" className="mt-2 w-full max-w-sm rounded border border-border" />
            )}
          </div>
          <div>
            <Label>Tags (comma-separated)</Label>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="travel, arizona" />
          </div>
          <div>
            <Label>Excerpt</Label>
            <Textarea rows={3} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          </div>
          <div>
            <Label>Minutes to read</Label>
            <Input
              type="number"
              min={0}
              value={form.minutes_to_read ?? ""}
              onChange={(e) => setForm({ ...form, minutes_to_read: e.target.value ? Number(e.target.value) : null })}
            />
          </div>
          <div>
            <Label>Content (HTML)</Label>
            <Textarea
              rows={20}
              className="font-mono text-xs"
              value={form.content_html}
              onChange={(e) => setForm({ ...form, content_html: e.target.value })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              HTML is rendered as-is on the post page. Use &lt;p&gt;, &lt;h2&gt;, &lt;img&gt;, &lt;a&gt; etc.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RestoreWixListsButton({ onDone }: { onDone: () => void }) {
  const restoreFn = useServerFn(adminRestoreWixLists);
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={async () => {
        if (!confirm("Fetch missing list content from Wix and update all affected posts?")) return;
        setBusy(true);
        try {
          const reports = await restoreFn();
          const restored = reports.filter((r) => r.status === "restored").length;
          const coverOnly = reports.filter((r) => r.status === "cover-only").length;
          const skipped = reports.filter((r) => r.status === "skipped");
          const errored = reports.filter((r) => r.status === "error");
          toast.success(
            `Restored ${restored} posts (+ ${coverOnly} cover-only). ${skipped.length} skipped, ${errored.length} errors.`,
          );
          if (skipped.length || errored.length) {
            console.warn("Restore report:", reports);
          }
          onDone();
        } catch (e: any) {
          toast.error(e?.message ?? "Restore failed");
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? "Restoring…" : "Restore lists from Wix"}
    </Button>
  );
}
